import React, { useContext } from 'react'
import styled from 'styled-components'
import GatsbyImage from 'gatsby-image'
import { LayoutContext } from '../../contexts'
import { SiteLink } from '../shared/SiteLink'
import RichText from '../RichText'
import { useBlogPostClickedTracking } from '../../hooks/tracking'
import { getAvailable } from '../../utils'
import BreadCrumb from '../shared/BreadCrumb'
import { useGlobal } from '../../hooks/useGlobal'

const Article = ({
  title,
  type,
  date,
  content,
  relatedBlogpost,
  slug,
  location,
}) => {
  const { currency, node_locale } = useContext(LayoutContext)

  const {
    layout: { home, relatedArticles },
  } = useGlobal(['layout'], node_locale)

  return (
    <StyledArticle>
      <Header>
        <BreadCrumb>
          <SiteLink>{home}</SiteLink>
          {' / '}
          <SiteLink to='blog'>Blog</SiteLink> / {title}
        </BreadCrumb>
      </Header>
      <div>
        <Label>{type}</Label>
        <Title>{title}</Title>
        {date && <StyledDate>{date.slice(0, 10)}</StyledDate>}
        <Content>{content && <RichText json={content} />}</Content>
      </div>
      {relatedBlogpost && (
        <RelatedPosts>
          <RelatedTitle>{relatedArticles}</RelatedTitle>
          {relatedBlogpost
            .filter(getAvailable)
            .map(({ image, slug, type, title }, index) => {
              return (
                <RelatedPost
                  to={slug}
                  key={index}
                  onClick={() =>
                    useBlogPostClickedTracking({
                      currency: currency?.code,
                      locale: node_locale,
                      postType: type,
                      name: title,
                      position: index + 1,
                      destination: `/${node_locale}/${slug}/`,
                      linkType: 'Image',
                      viewType: 'Blog Post Page',
                      sectionType: 'Related Blog Posts',
                    })
                  }
                  state={{ referrer: location.pathname }}
                >
                  <ImageWrapper>
                    {image?.fluid && (
                      <StyledImage
                        fluid={image.fluid}
                        alt={image.title ?? ''}
                        loading='eager'
                      />
                    )}
                    <Label small>{type}</Label>
                  </ImageWrapper>

                  <PostTitle>{title}</PostTitle>
                </RelatedPost>
              )
            })}
        </RelatedPosts>
      )}
    </StyledArticle>
  )
}

const StyledArticle = styled.section`
  padding: ${(props) => props.theme.space.marginMobile}vw
    ${(props) => props.theme.space.marginMobile}vw
    ${(props) => props.theme.space.marginMobile * 5}vw;
  overflow: hidden;
  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    padding: 0 ${(props) => props.theme.space.marginDesktop * 10}vw
      ${(props) => props.theme.space.marginDesktop * 5}vw;
    display: grid;
    grid-template-columns: 5fr 2fr;
    grid-column-gap: ${(props) => props.theme.space.marginDesktop * 5}vw;
  }
`

const Header = styled.div`
  display: none;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    grid-column: 1 / -1;
    display: block;
    font-size: ${(props) => props.theme.fontSize.d12};
    color: #a2a2a2;
    padding: ${(props) => props.theme.space.marginDesktop * 4}vw 0
      ${(props) => props.theme.space.marginDesktop * 5}vw;
  }
`

const Label = styled.div`
  position: ${(props) => (props.small ? 'absolute' : 'relative')};
  bottom: 0;
  left: 0;
  font-size: 2.93333vw;
  display: inline-block;
  background: ${(props) => props.theme.colors.blue};
  text-align: center;
  color: white;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: ${(props) => props.theme.space.marginMobile / 2}vw
    ${(props) => props.theme.space.marginMobile / 2}vw;
  margin-bottom: ${(props) =>
    props.small ? 0 : props.theme.space.marginMobile}vw;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    font-size: ${(props) => props.theme.fontSize.d12};
    padding: ${(props) => props.theme.space.marginDesktop}vw
      ${(props) => props.theme.space.marginDesktop}vw;
    margin-bottom: ${(props) =>
      props.small ? 0 : props.theme.space.marginDesktop}vw;
  }
`

const Title = styled.h1`
  font-size: 7.3333vw;
  line-height: 8.6666vw;
  color: ${(props) => props.theme.colors.blue};
  padding-bottom: ${(props) => props.theme.space.marginMobile}vw;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    font-size: 3.125vw;
    line-height: 3.8194vw;
    padding-bottom: ${(props) => props.theme.space.marginDesktop * 2}vw;
  }
`

const StyledDate = styled.time`
  color: #a2a2a2;
  display: block;
  font-size: ${(props) => props.theme.fontSize.m20};
  padding-bottom: ${(props) => props.theme.space.marginMobile}vw;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    font-size: ${(props) => props.theme.fontSize.d12};
    margin-bottom: ${(props) => props.theme.space.marginDesktop * 2}vw;
    padding-bottom: ${(props) => props.theme.space.marginDesktop * 2}vw;
  }
`

const Content = styled.div`
  h4 {
    font-size: ${(props) => props.theme.fontSize.m30};
    line-height: 5.3333vw;
    padding-bottom: ${(props) => props.theme.space.marginMobile * 2}vw;
  }

  h2 {
    font-size: ${(props) => props.theme.fontSize.m26};
    font-family: ${(props) => props.theme.fontFamily.bold};
    line-height: 4.8vw;
    padding-bottom: ${(props) => props.theme.space.marginMobile}vw;
  }

  p,
  li {
    font-size: ${(props) => props.theme.fontSize.m23};
    line-height: 4.4vw;
  }

  p {
    padding-bottom: ${(props) => props.theme.space.marginMobile / 2}vw;
  }

  img {
    max-width: 100%;
  }

  a {
    display: inline-block;
    color: ${(props) => props.theme.colors.blue};
  }

  iframe {
    max-width: 100%;
  }

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    max-width: 80%;
    h4 {
      font-size: ${(props) => props.theme.fontSize.d20};
      line-height: 2.083vw;
      padding-bottom: ${(props) => props.theme.space.marginDesktop * 2}vw;
    }

    h2 {
      font-size: 1.1111vw;
      line-height: 1.805vw;
      padding-bottom: ${(props) => props.theme.space.marginDesktop * 2}vw;
    }

    p,
    li {
      font-size: ${(props) => props.theme.fontSize.d14};
      line-height: 1.6667vw;
    }

    p {
      padding-bottom: ${(props) => props.theme.space.marginDesktop * 2}vw;
    }
  }
`

const RelatedPosts = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-gap: ${(props) => props.theme.space.marginMobile}vw;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    display: flex;
    flex-direction: column;
    grid-template-columns: repeat(3, 1fr);
    grid-gap: ${(props) => props.theme.space.marginDesktop * 8}vw;
    padding: ${(props) => props.theme.space.marginDesktop * 2}vw
      ${(props) => props.theme.space.marginDesktop * 2}vw;
  }
`

const RelatedTitle = styled.span`
  display: none;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    display: block;
    font-size: ${(props) => props.theme.fontSize.d20};
    line-height: 2.083vw;
    padding-top: ${(props) => props.theme.space.marginDesktop * 3}vw;
    padding-bottom: ${(props) => props.theme.space.marginDesktop * 3}vw;
  }
`

const RelatedPost = styled(SiteLink)`
  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    padding-bottom: ${(props) => props.theme.space.marginDesktop * 6}vw;
  }
`

const ImageWrapper = styled.div`
  position: relative;
  margin-bottom: ${(props) => props.theme.space.marginMobile / 2}vw;
  height: 60vw;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    height: 30vw;
    margin-bottom: ${(props) => props.theme.space.marginDesktop * 2}vw;
  }
`

const StyledImage = styled(GatsbyImage)`
  z-index: -1;
  height: 100%;
`

const PostTitle = styled.h3`
  font-size: ${(props) => props.theme.space.m23};
  line-height: 4.8vw;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    font-size: 1.18vw;
    line-height: 1.875vw;
  }
`

export default Article
